import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import type { TypedSchema } from '../utils/typedSchemaType';
import { getTextAreaStringSchema } from '../utils/getTextAreaStringSchema';

export type PrickTestProperty = {
    id: number;
    title: string;
    value: string | number;
};

export const savePrickTestMeasurementsExplanationKeys = {
    aanvullendOnderzoek: 'aanvullend onderzoek',
    conclusie: 'conclusie', // CS-23280: Temporary fix until Evidencio fixed it on their end
    negatieveUitslagen: 'negatieve uitslagen',
    positieveUitslagen: 'positieve uitslagen',
} as const;

export type SavePrickTestMeasurementsRequestBody = {
    explanation: {
        type: (typeof savePrickTestMeasurementsExplanationKeys)[keyof typeof savePrickTestMeasurementsExplanationKeys];
        value: string[];
    }[];
    properties: PrickTestProperty[];
};

export type SaveDecisionTreeResultBody = {
    decisionTreeResult: Record<string, unknown>;
};

export const savePrickTestMeasurementsRequestBodyValidation: TypedSchema<SavePrickTestMeasurementsRequestBody> = {
    explanation: Koi.array()
        .items(
            Koi.object({
                type: Koi.string().valid(...Object.values(savePrickTestMeasurementsExplanationKeys)),
                value: Koi.array().items(Koi.string().max(10_000)).min(1).max(100).required(),
            })
        )
        .min(1)
        .max(100),
    properties: Koi.array()
        .items(
            Koi.object({
                id: Koi.number().integer().required(),
                title: Koi.string().max(10000).required(),
                value: Koi.alternatives().try(Koi.string().max(10000).allow(''), Koi.number()).optional(),
            })
        )
        .min(1)
        .max(100),
};

export const saveDecisionTreeResultBodySchemaValidation: TypedSchema<SaveDecisionTreeResultBody> = {
    decisionTreeResult: Koi.object(),
};

export const validationOptions: Joi.ValidationOptions = {
    allowUnknown: true,
    convert: false,
    presence: 'required',
};

export const savePrickTestMeasurementsRequestBodySchema = Koi.object(
    savePrickTestMeasurementsRequestBodyValidation
).options({
    allowUnknown: true,
    convert: false,
});

export type SurveyQuestionnaireStringAnswer = {
    value: string | number;
};

export type SurveyQuestionnaireOptionsAnswer = {
    selected: boolean;
    value: string;
};

export type SurveyQuestionnaireQuestion = {
    answer: SurveyQuestionnaireStringAnswer | SurveyQuestionnaireOptionsAnswer[];
    category: string;
    question: string;
};

export type SaveQuestionnaireResponseRequestBody = {
    date: string;
    surveyQuestionnaire: SurveyQuestionnaireQuestion[];
};

export type SsoRole = 'GP' | 'Grader';

export type GetSsoDataBody = {
    patientId: string;
    ssoRole: SsoRole;
};

export const getSsoDataBodyValidation: TypedSchema<GetSsoDataBody> = {
    patientId: Koi.string().uuid().required(),
    ssoRole: Koi.string().valid('GP', 'Grader').required(),
};

const surveyQuestionnaireStringAnswerValidation: TypedSchema<SurveyQuestionnaireStringAnswer> = {
    value: Koi.alternatives().try(getTextAreaStringSchema(), Koi.number()),
};

const surveyQuestionnaireOptionsAnswerValidation: TypedSchema<SurveyQuestionnaireOptionsAnswer> = {
    selected: Koi.boolean().required(),
    value: getTextAreaStringSchema(),
};

export const saveQuestionnaireResponseRequestBodyValidation: TypedSchema<SaveQuestionnaireResponseRequestBody> = {
    date: Koi.string().isoDate().required(),
    surveyQuestionnaire: Koi.array().items(
        Koi.object({
            answer: Koi.alternatives()
                .try(
                    Koi.object(surveyQuestionnaireStringAnswerValidation),
                    Koi.array()
                        .items(Koi.object(surveyQuestionnaireOptionsAnswerValidation))
                        .custom((value, helpers) => {
                            const selected = value.filter((entry: SurveyQuestionnaireOptionsAnswer) => entry.selected);

                            return selected.length !== 1 ? helpers.error('any.invalid') : value;
                        })
                )
                .required(),
            category: getTextAreaStringSchema().required(),
            question: getTextAreaStringSchema().required(),
        })
            .min(1)
            .required()
    ),
};

export const getSsoDataBodySchema = Koi.object(getSsoDataBodyValidation).options(validationOptions);
export const saveQuestionnaireResponseRequestBodySchema = Koi.object(saveQuestionnaireResponseRequestBodyValidation);
export const saveDecisionTreeResultBodySchema = Koi.object(saveDecisionTreeResultBodySchemaValidation).options(
    validationOptions
);
